import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
  Icon,
} from "@chakra-ui/react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import { FaUsers, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { MdSave } from "react-icons/md";
import { motion } from "framer-motion";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="8px"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => {
  const navigate = useNavigate();
  return (
    <MotionFlex
      direction="column"
      align="center"
      justify="center"
      py={12}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Icon as={FaUsers} boxSize={16} color="#f57d36" mb={4} />
      <Text fontSize="xl" fontWeight="bold" mb={2}>
        No Private Billing Configurations Found
      </Text>
      <Text color="gray.500" textAlign="center" maxW="md" mb={6}>
        There are no private billing configurations for this station. Configure
        user profiles to set custom pricing.
      </Text>
      <Button
        size="sm"
        height="36px"
        px={6}
        bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
        color="white"
        borderRadius="8px"
        boxShadow="0 4px 12px rgba(245, 125, 54, 0.3)"
        fontWeight="600"
        _hover={{
          bgGradient: "linear(135deg, #f35626 0%, #f57d36 100%)",
          boxShadow: "0 6px 16px rgba(245, 125, 54, 0.4)",
          transform: "translateY(-2px)",
        }}
        _active={{
          transform: "translateY(0)",
          boxShadow: "0 2px 8px rgba(245, 125, 54, 0.3)",
        }}
        transition="all 0.2s ease"
        onClick={() => navigate("/admin/userprofiles")}
      >
        Configure now
      </Button>
    </MotionFlex>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const PrivateBillingRow = ({
  row,
  index,
  onUpdate,
  isUpdating,
  textColor,
  borderColor,
}) => {
  const [price, setPrice] = useState(row.Price);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    setPrice(row.Price);
    setHasChanged(false);
  }, [row.Price]);

  const handlePriceChange = (e) => {
    const newPrice = e.target.value;
    setPrice(newPrice);
    setHasChanged(parseFloat(newPrice) !== row.Price);
  };

  const handleSave = async () => {
    await onUpdate(row.UserGroupId, price);
    setHasChanged(false);
  };

  return (
    <MotionTr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      _hover={{
        bg: "rgba(245, 125, 54, 0.05)",
      }}
      transition="all 0.2s"
    >
      <Td borderColor={borderColor}>
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {index + 1}
        </Text>
      </Td>
      <Td borderColor={borderColor}>
        <Flex align="center" gap={2}>
          <Icon as={FaUsers} color="#f57d36" />
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {row.Name}
          </Text>
        </Flex>
      </Td>
      <Td borderColor={borderColor}>
        <Text color={textColor} fontSize="sm">
          {row.ContractId || "N/A"}
        </Text>
      </Td>
      <Td borderColor={borderColor}>
        <Input
          value={price}
          onChange={handlePriceChange}
          type="number"
          step="0.01"
          size="sm"
          width="120px"
          focusBorderColor="#f57d36"
          bg="white"
          borderRadius="6px"
          border="1px solid rgba(245, 125, 54, 0.2)"
          _hover={{
            borderColor: "#f57d36",
          }}
          _focus={{
            boxShadow: "0 0 0 1px #f57d36",
          }}
        />
      </Td>
      <Td borderColor={borderColor}>
        <Button
          size="sm"
          leftIcon={<MdSave />}
          bg="#f57d36"
          color="white"
          onClick={handleSave}
          isLoading={isUpdating}
          isDisabled={!hasChanged}
          borderRadius="6px"
          _hover={{
            bg: "#ff5c00",
            transform: "translateY(-2px)",
          }}
          _disabled={{
            opacity: 0.4,
            cursor: "not-allowed",
          }}
          transition="all 0.2s"
        >
          Save
        </Button>
      </Td>
    </MotionTr>
  );
};

const PrivateBillingTable = ({ data = [], onUpdate, isUpdating }) => {
  const [searchQuery, setSearchQuery] = useState("");

  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        accessor: (row, index) => index + 1,
        id: "serialNumber",
      },
      {
        Header: "User Profile",
        accessor: "Name",
      },
      {
        Header: "Contract ID",
        accessor: "ContractId",
      },
      {
        Header: "Price (₹/kWh)",
        accessor: "Price",
      },
      {
        Header: "Actions",
        accessor: "actions",
      },
    ],
    []
  );

  const tableData = useMemo(() => data, [data]);

  const tableInstance = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const name = row.original.Name || "";
          const contractId = row.original.ContractId || "";
          const price = row.original.Price?.toString() || "";

          return (
            name.toLowerCase().includes(lowercasedQuery) ||
            contractId.toLowerCase().includes(lowercasedQuery) ||
            price.includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
  } = tableInstance;

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "#f57d36";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  if (data.length === 0) {
    return (
      <GlassMorphismCard p={0}>
        <EmptyStateIllustration />
      </GlassMorphismCard>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      <GlassMorphismCard p={0}>
        {/* Header with search */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack
              align="start"
              spacing={2}
              width={{ base: "100%", md: "auto" }}
            >
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Private Billing
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, contract ID, price..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>

            <Flex align="center" justify="flex-end">
              <Text fontSize="sm" fontWeight="medium" color="gray.500">
                Total: {data.length}{" "}
                {data.length === 1 ? "profile" : "profiles"}
              </Text>
            </Flex>
          </Flex>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table {...getTableProps()} variant="simple" size="md">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, idx) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                  {headerGroup.headers.map((column, colIdx) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={colIdx}
                      color={textColor}
                      fontSize="xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      borderColor={borderColor}
                      cursor={column.canSort ? "pointer" : "default"}
                      _hover={column.canSort ? { bg: hoverBg } : {}}
                      width={column.id === "serialNumber" ? "80px" : "auto"}
                    >
                      <Flex align="center" gap={2}>
                        {column.render("Header")}
                        {column.canSort && (
                          <Icon
                            as={
                              column.isSorted
                                ? column.isSortedDesc
                                  ? FaSortDown
                                  : FaSortUp
                                : FaSort
                            }
                            boxSize={3}
                            color={column.isSorted ? themeColor : "gray.400"}
                          />
                        )}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, rowIdx) => {
                prepareRow(row);
                return (
                  <PrivateBillingRow
                    key={row.original.UserGroupId || rowIdx}
                    row={row.original}
                    index={pageIndex * pageSize + rowIdx}
                    onUpdate={onUpdate}
                    isUpdating={isUpdating}
                    textColor={textColor}
                    borderColor={borderColor}
                  />
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box p={6} borderTop="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={4}
          >
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.600">
                Rows per page:
              </Text>
              <Select
                size="sm"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                width="80px"
                borderRadius="md"
                _focus={{
                  borderColor: themeColor,
                  boxShadow: `0 0 0 1px ${themeColor}`,
                }}
              >
                {[5, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </HStack>

            <HStack spacing={2}>
              <PaginationButton
                onClick={() => gotoPage(0)}
                isDisabled={!canPreviousPage}
              >
                <ChevronLeftIcon />
                <ChevronLeftIcon ml={-2} />
              </PaginationButton>
              <PaginationButton
                onClick={() => previousPage()}
                isDisabled={!canPreviousPage}
              >
                <ChevronLeftIcon />
              </PaginationButton>

              {[...Array(pageCount)].map((_, idx) => {
                if (
                  idx === 0 ||
                  idx === pageCount - 1 ||
                  (idx >= pageIndex - 1 && idx <= pageIndex + 1)
                ) {
                  return (
                    <PaginationButton
                      key={idx}
                      onClick={() => gotoPage(idx)}
                      isActive={pageIndex === idx}
                    >
                      {idx + 1}
                    </PaginationButton>
                  );
                } else if (idx === pageIndex - 2 || idx === pageIndex + 2) {
                  return (
                    <Text key={idx} px={2} color="gray.500">
                      ...
                    </Text>
                  );
                }
                return null;
              })}

              <PaginationButton
                onClick={() => nextPage()}
                isDisabled={!canNextPage}
              >
                <ChevronRightIcon />
              </PaginationButton>
              <PaginationButton
                onClick={() => gotoPage(pageCount - 1)}
                isDisabled={!canNextPage}
              >
                <ChevronRightIcon />
                <ChevronRightIcon ml={-2} />
              </PaginationButton>
            </HStack>

            <Text fontSize="sm" color="gray.600">
              Page {pageIndex + 1} of {pageCount || 1}
            </Text>
          </Flex>
        </Box>
      </GlassMorphismCard>
    </MotionBox>
  );
};

export default PrivateBillingTable;
